import { forwardRef, useId } from "react";
import { cn } from "@/lib/cn";

/**
 * Shared control chrome. `w-full` lives here so callers get a sensible default,
 * but note that a `w-[…]`/`h-[…]` passed via className can lose the cascade
 * against `w-full`: wrap the control in a sized div instead when you need an
 * exact width (see the time inputs in the event/reminder dialogs).
 */
const CONTROL =
  "w-full min-w-0 rounded-lg border border-line bg-paper px-3 text-sm text-ink outline-none transition placeholder:text-ink-faint focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:cursor-not-allowed disabled:opacity-60";

export const Input = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(CONTROL, "h-9", className)} {...rest} />;
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(CONTROL, "resize-y py-2 leading-6", className)}
      {...rest}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={cn(CONTROL, "h-9 cursor-pointer pr-8", className)}
      {...rest}
    >
      {children}
    </select>
  );
});

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

/** Label + control + hint, with consistent vertical rhythm. */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <label
        htmlFor={htmlFor}
        className="block text-[12px] font-medium text-ink-soft"
      >
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[11px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}

/** Convenience wrapper that wires up the label's `htmlFor` automatically. */
export function LabelledInput({
  label,
  hint,
  error,
  className,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string;
}) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} className={className}>
      <Input id={id} aria-invalid={!!error} {...rest} />
    </Field>
  );
}
